import React from "react";
import { Accordion } from "react-bootstrap";
import { Link } from "react-router-dom";
import debounce from "lodash.debounce";
import { AccordionSkeleton } from "../../components";
import { ApiHelper, ArrayHelper, MessageInterface, ToastHelper } from "../../helpers";


interface Props {
  refreshKey: string;
}

export const MessagesSidebar: React.FC<Props> = (props) => {
  const [messages, setMessages] = React.useState<MessageInterface[]>(null);
  const [selectedMessage, setSelectedMessage] = React.useState<MessageInterface>(null);
  const [filteredMessages, setFilteredMessages] = React.useState<MessageInterface[]>(null);
  const [searchActive, setSearchActive] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showUnusedMessages, setShowUnusedMessages] = React.useState(false);

  const init = () => {
    ApiHelper.get<MessageInterface[]>("/messages")
      .then((data) => {
        setMessages(data || []);
        setFilteredMessages(data || []);
      })
      .catch(err => {
        console.error("Failed to load messages:", err);
        ToastHelper.error("Failed to load messages");
      });
  };

  const updateSelected = () => {
    let result: MessageInterface = null;
    const parts = window.location.href.split("?")[0].split("/");
    const id = parseInt(parts[parts.length - 1]);
    messages?.forEach((m) => {
      if (m.id === id) result = m;
    });
    setSelectedMessage(result);
  };

  React.useEffect(init, [props.refreshKey]);
  React.useEffect(updateSelected, [messages]);

  const applyFilters = (term: string) => {
    let filtered = messages;
    if (term) {
      filtered = filtered?.filter((m) => m.name?.toLowerCase().includes(term));
    }
    setFilteredMessages(filtered);
  };

  // Debounced filter application - prevents filtering on every keystroke
  const debouncedApplyFilters = React.useMemo(
    () => debounce(applyFilters, 300),
    [messages] // eslint-disable-line
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    debouncedApplyFilters(term);
  };

  const handleToggleMessages = () => {
    setShowUnusedMessages((prevState) => {
      const newState = !prevState;
      if (newState) {
        const unusedMessages = messages?.filter((m) => m.isExistInPlaylist === 0);
        setFilteredMessages(unusedMessages);
      } else {
        setFilteredMessages(messages);
      }
      return newState;
    });
  };

  const getMessages = () => {
    if (filteredMessages === null) return <AccordionSkeleton sections={4} />;
    else return getMessageAccordion();
  };

  const getMessageAccordion = () => {
    if (filteredMessages) {
      const sections: React.ReactElement[] = [];
      filteredMessages?.forEach((m) => {
        if (!m.category) m.category = "Uncategorized";
      });
      const categories = ArrayHelper.getStrings(filteredMessages, "category");
      categories.forEach((c) => {
        const catMessages = ArrayHelper.getAll(filteredMessages, "category", c);
        const catContent = getMessagesRows(catMessages);
        sections.push(getSection(c, c, catContent));
      });
      const defaultActiveKey = categories.length > 5 ? "" : categories[0];
      return (
          <Accordion id="messagesSidebar" defaultActiveKey={defaultActiveKey}>
            {sections}
          </Accordion>
      );
    }
  };

  const getSection = (eventKey: string, title: string, contents: React.ReactElement) => {
    return (
        <Accordion.Item key={eventKey} eventKey={eventKey}>
          <Accordion.Header>{title}</Accordion.Header>
          <Accordion.Body>{contents}</Accordion.Body>
        </Accordion.Item>
    );
  };

  const getMessagesRows = (messages: MessageInterface[]) => {
    const result: React.ReactElement[] = [];
    messages.forEach((m) => {
      let url = "/messages/" + m.id;
      if (m.shape !== "16:9") url += "?shape=" + m.shape;
      const className = m.id === selectedMessage?.id ? "active" : "";
      result.push(
          <li key={"m" + m.id} className="nav-item">
            <Link className={className} to={url}>
              {m.name || "(no name)"}
            </Link>
          </li>
      );
    });
    return <ul className="nav flex-column">{result}</ul>;
  };

  return (
      <>
        <div className="titlebar">
          {searchActive ? (
              <div className="search-container" style={{position: "relative"}}>
                <input
                    data-testid="messages-search-input"
                    type="text"
                    className="form-control"
                    placeholder="Search messages..."
                    value={searchTerm}
                    onChange={handleSearch}
                    onBlur={() => {
                      if (!searchTerm) setSearchActive(false);
                    }}
                />
                <div
                    className="unused-messages-link"
                    style={{
                      position: "absolute",
                      bottom: "-30px",
                      left: "0",
                      width: "100%",
                      textAlign: "center",
                    }}
                >
                  <button
                      data-testid="messages-sidebar-action-button"
                      className="text-warning btn btn-link"
                      onClick={handleToggleMessages}
                  >
                    {showUnusedMessages
                        ? "Show All Messages"
                        : "Show Unused Messages"}
                  </button>
                </div>
              </div>
          ) : (
              <>
                <div className="float-right d-flex align-items-center">
                  <i
                      className="fas fa-search"
                      style={{cursor: "pointer", marginLeft: "10px", marginRight: "10px"}}
                      onClick={() => setSearchActive(true)}
                  ></i>
                  <Link to="/messages" className="btn btn-sm btn-success">
                    <i className="fas fa-plus"></i> New
                  </Link>
                </div>
                <div className="title">
                <i className="fas fa-list-ul"></i> Messages
                </div>
              </>
          )}
        </div>
        {getMessages()}
      </>
  );
};
